import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fmtDateTime } from '@em/shared';
import { api, qk, queryClient, useMyobStatus, useSettings } from '../../api';
import { useAuth } from '../../auth/AuthContext';
import { useToast } from '../../components/Toast';
import { Alert, Badge, Button, Card, ErrorAlert, Field, Input, QueryState, Select } from '../../components/ui';

export function MyobSettings() {
  const { can } = useAuth();
  const owner = can('owner');
  const [params, setParams] = useSearchParams();
  const result = params.get('result');
  const status = useMyobStatus();
  const settings = useSettings();
  const tz = settings.data?.defaultTimezone ?? 'Australia/Sydney';
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  const [match, setMatch] = useState<{ matched: number; unmatched: string[] } | null>(null);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['myob'] });
    void queryClient.invalidateQueries({ queryKey: ['export'] });
  };
  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    setActionError(null);
    try {
      await fn();
    } catch (e) {
      setActionError(e);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="stack">
      {result && (
        <Alert tone={result === 'connected' ? 'green' : 'red'} title={result === 'connected' ? 'MYOB connected' : 'MYOB connection did not complete'}>
          {result === 'connected' ? 'Now choose the company file below.' : result}
          <div style={{ marginTop: 6 }}>
            <Button size="sm" variant="ghost" onClick={() => setParams({}, { replace: true })}>
              Dismiss
            </Button>
          </div>
        </Alert>
      )}
      <ErrorAlert error={actionError} />
      <Card title="MYOB AccountRight connection">
        <QueryState q={status}>
          {(s) => (
            <div className="stack">
              <dl className="kv">
                <dt>Server set up</dt>
                <dd>{s.configured ? <Badge tone="green">Yes</Badge> : <Badge tone="amber">No: MYOB client ID and secret are not set on the server</Badge>}</dd>
                <dt>Connected</dt>
                <dd>{s.connected ? <Badge tone="green">Connected</Badge> : <Badge>Not connected</Badge>}</dd>
                <dt>Company file</dt>
                <dd>{s.companyFileName ?? <span className="muted">None chosen</span>}</dd>
                <dt>Last test</dt>
                <dd>
                  {s.lastTestAt ? (
                    <>
                      {fmtDateTime(s.lastTestAt, tz)} <Badge tone={s.lastTestOk ? 'green' : 'red'}>{s.lastTestOk ? 'OK' : 'Failed'}</Badge>
                      {s.lastTestMessage && <div className="small muted">{s.lastTestMessage}</div>}
                    </>
                  ) : (
                    <span className="muted">Never</span>
                  )}
                </dd>
              </dl>
              <div className="row">
                {owner && (
                  <Button
                    variant={s.connected ? 'default' : 'primary'}
                    loading={busy === 'connect'}
                    disabled={!s.configured}
                    onClick={() =>
                      run('connect', async () => {
                        const { url } = await api.myobConnectUrl();
                        window.location.assign(url);
                      })
                    }
                  >
                    {s.connected ? 'Reconnect MYOB' : 'Connect MYOB'}
                  </Button>
                )}
                <Button
                  loading={busy === 'test'}
                  disabled={!s.connected}
                  onClick={() =>
                    run('test', async () => {
                      const r = await api.myobTest();
                      refresh();
                      if (r.lastTestOk) toast.success('Connection works');
                      else toast.push({ kind: 'error', title: 'Connection test failed', body: r.lastTestMessage ?? undefined });
                    })
                  }
                >
                  Test connection
                </Button>
                <Button
                  loading={busy === 'match'}
                  disabled={!s.connected || !s.companyFileUri}
                  onClick={() =>
                    run('match', async () => {
                      const r = await api.myobAutoMatch();
                      setMatch(r);
                      void queryClient.invalidateQueries({ queryKey: ['employees'] });
                      refresh();
                    })
                  }
                >
                  Auto-match employees
                </Button>
              </div>
              {!owner && <p className="small muted">Only the owner can connect MYOB or change the company file.</p>}
            </div>
          )}
        </QueryState>
      </Card>
      {match && (
        <Alert tone={match.unmatched.length ? 'amber' : 'green'} title={`${match.matched} employee${match.matched === 1 ? '' : 's'} matched`}>
          {match.unmatched.length > 0 && (
            <>
              Not matched (set their MYOB card ID, then try again):
              <ul>
                {match.unmatched.map((n) => (
                  <li key={n}>{n}</li>
                ))}
              </ul>
            </>
          )}
        </Alert>
      )}
      {owner && status.data?.connected && <CompanyFilePicker current={status.data.companyFileUri} />}
    </div>
  );
}

function CompanyFilePicker({ current }: { current: string | null }) {
  const files = useQuery({ queryKey: qk.myobFiles, queryFn: api.myobCompanyFiles, retry: false });
  const toast = useToast();
  const [uri, setUri] = useState(current ?? '');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const f = files.data?.find((x) => x.uri === uri);
    if (!f) return;
    setBusy(true);
    setError(null);
    try {
      await api.myobSelectCompanyFile({ uri: f.uri, name: f.name, username: username || undefined, password: password || undefined });
      setPassword('');
      void queryClient.invalidateQueries({ queryKey: ['myob'] });
      toast.success('Company file saved', 'Run a connection test to check the credentials.');
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card title="Company file">
      <QueryState q={files}>
        {(list) => (
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault();
              void save();
            }}
          >
            <ErrorAlert error={error} />
            <Field label="Company file">
              <Select value={uri} onChange={(e) => setUri(e.target.value)}>
                <option value="">Choose a company file</option>
                {list.map((f) => (
                  <option key={f.uri} value={f.uri}>
                    {f.name}
                  </option>
                ))}
              </Select>
            </Field>
            <div className="form-grid">
              <Field label="Company file username" hint="Optional; leave blank to keep the saved one">
                <Input autoComplete="off" value={username} onChange={(e) => setUsername(e.target.value)} />
              </Field>
              <Field label="Company file password">
                <Input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
              </Field>
            </div>
            <div className="row end">
              <Button type="submit" variant="primary" loading={busy} disabled={!uri}>
                Save company file
              </Button>
            </div>
          </form>
        )}
      </QueryState>
    </Card>
  );
}
